import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getAllCallBetOrders } from "../../actions/gameSettings";
import { formatCallBetCardTime } from "../../lib/dateFormat";
import { formatMoney } from "../../lib/wagerFinancials";
import { formatOdds } from "../../../utility/odds";
import { formatWagerAmount } from "../../../utility/wagerDisplay";
import { isDTS } from "../../../utility/platform";
import { getFeaturedMarketLabel, getTerm } from "../../../utility/complianceCopy";
import { callBetMarketLabel } from "../../../utility/callBetDisplay";
import { loadCallEntriesFailedMessage } from "../../../utility/entryCopy";
import Loader from "../Loader";
import WagerEditModal from "../WagerEditModal";
import "./callBetsBetBuilderPanel.css";

const marketLabel = callBetMarketLabel;

const statusLabel = (status) => {
  const s = String(status || "").trim();
  if (!s || s === "NotActivated") return "Pending activation";
  return s.replace(/([A-Z])/g, " $1").trim();
};

const formatTarget = (item) => {
  if (item.oddType === "h2h") {
    const target = item.desiredPrice || item.price;
    return `${item.teamName} @ ${formatOdds(target)}`;
  }
  const targetPoint = item.point;
  return `${item.teamName} ${targetPoint} @ ${formatOdds(item.price)}`;
};

const formatBook = (item) => {
  if (item.oddType === "h2h") {
    return formatOdds(item.originalPrice ?? item.price);
  }
  const bookPoint = item.originalPoint ?? item.point;
  return `${bookPoint} @ ${formatOdds(item.originalPrice ?? item.price)}`;
};

export default function CallBetsBetBuilderPanel({ isActive = true, refreshKey = 0 }) {
  const accountData = useSelector((state) => state.account);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showEdit, setShowEdit] = useState(false);

  const fetchOrders = useCallback(async () => {
    const email = accountData?.emailId;
    if (!email) {
      setOrders([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { status, result, message } = await getAllCallBetOrders({ email });
      if (status === true && Array.isArray(result)) {
        const sorted = [...result].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        setOrders(sorted);
      } else {
        setOrders([]);
        if (message && !message.toLowerCase().includes("no pending")) {
          setError(message);
        }
      }
    } catch {
      setOrders([]);
      setError(loadCallEntriesFailedMessage({ retry: true }));
    } finally {
      setLoading(false);
    }
  }, [accountData?.emailId]);

  useEffect(() => {
    if (isActive) fetchOrders();
  }, [isActive, fetchOrders, refreshKey]);

  const openEdit = (order) => {
    setSelectedOrder(order);
    setShowEdit(true);
  };

  const closeEdit = () => {
    setSelectedOrder(null);
    setShowEdit(false);
  };

  if (loading) {
    return (
      <div className="call-bets-panel call-bets-panel--loading">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="call-bets-panel">
        <p className="call-bets-panel__error">{error}</p>
        <button type="button" className="btn img_btn getbtn" onClick={fetchOrders}>
          Retry
        </button>
      </div>
    );
  }

  if (!orders.length) {
    return (
      <div className="call-bets-panel">
        <p className="call-bets-panel__empty">
          {isDTS()
            ? "No active Target picks"
            : "No active call bets"}
        </p>
        <p className="call-bets-panel__hint fs12 text-center">
          Select a match from the board above, then set your desired line or{" "}
          {getFeaturedMarketLabel("h2h", isDTS())} price in the form above.
        </p>
      </div>
    );
  }

  return (
    <div className="call-bets-panel">
      <p className="call-bets-panel__section-title fs12">
        {isDTS()
          ? "Pending Target picks"
          : "Pending call bets"}
      </p>
      <div className="call-bets-panel__header">
        <p className="call-bets-panel__count">{orders.length} pending</p>
        <button
          type="button"
          className="call-bets-panel__refresh fs12"
          onClick={fetchOrders}
        >
          Refresh
        </button>
      </div>

      <ul className="call-bets-panel__list">
        {orders.map((item) => {
          const risk = Number(item.wagerAmount) || 0;
          const payout = Number(item.payOut) || 0;
          const win = Math.max(0, payout - risk);

          return (
            <li key={item._id || item.ticketId} className="call-bet-card">
              <div className="call-bet-card__top">
                <span className="call-bet-card__ticket">#{item.ticketId}</span>
                <span className="call-bet-card__status">
                  {statusLabel(item.betstatus)}
                </span>
              </div>

              <p className="call-bet-card__sport">{item.title || item.sport_title}</p>

              <p className="call-bet-card__match">
                {item.homeTeam} <span className="call-bet-card__vs">vs</span>{" "}
                {item.awayTeam}
              </p>

              <div className="call-bet-card__pick">
                <span className="call-bet-card__pick-label">
                  {marketLabel(item.oddType)}
                </span>
                <span className="call-bet-card__pick-value">
                  Want: {formatTarget(item)}
                </span>
              </div>

              <p className="call-bet-card__book fs11">
                Book at placement: {formatBook(item)}
              </p>

              <div className="call-bet-card__grid call-bet-card__grid--two">
                <div>
                  <span className="call-bet-card__meta-label">Risk</span>
                  <span className="call-bet-card__meta-value">
                    {formatWagerAmount(formatMoney(risk), item.fundingCoinBucket)}
                  </span>
                </div>
                <div>
                  <span className="call-bet-card__meta-label">Win</span>
                  <span className="call-bet-card__meta-value call-bet-card__meta-value--win">
                    {formatWagerAmount(formatMoney(win), item.fundingCoinBucket)}
                  </span>
                </div>
              </div>

              <p className="call-bet-card__date">
                {formatCallBetCardTime(item.gameTime, item.createdAt)}
              </p>

              <button
                type="button"
                className="btn img_btn getbtn call-bet-card__edit"
                onClick={() => openEdit(item)}
              >
                Edit
              </button>
            </li>
          );
        })}
      </ul>

      <WagerEditModal
        show={showEdit}
        handleClose={closeEdit}
        item={selectedOrder}
        refreshData={fetchOrders}
      />
    </div>
  );
}
